/**
 * useNavConfig — Dynamic sidebar navigation hook
 *
 * Fetches the resolved sidebar layout from /api/organizations/{orgId}/nav-config.
 * Returns sections with items filtered by role access, respecting org overrides.
 * Caches with 1-minute TTL; auto-refreshes on pagePermissionsUpdated events.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/apiClient';
import { parseApiError } from '@/utils/apiErrors';
import useRefreshOnFocus from '@/hooks/useRefreshOnFocus';

// ============================================================================
// TYPES
// ============================================================================

export interface NavConfigItem {
  page_key: string;
  nav_tab_id: string;
  label: string;
  label_es?: string;
  icon?: string;
  has_access: boolean;
  display_behavior: string;
  display_order: number;
  pinned: boolean;
}

export interface NavConfigSection {
  section_key: string;
  section_name: string;
  section_name_es?: string;
  icon?: string;
  display_order: number;
  items: NavConfigItem[];
}

export interface NavConfigResponse {
  sections: NavConfigSection[];
}

// ============================================================================
// CACHE
// ============================================================================

const navConfigCache = new Map<string, {
  data: NavConfigResponse;
  timestamp: number;
}>();

const CACHE_TTL = 60000; // 1 minute

/** One nav-config failure used to cost the user their real sidebar for the whole
 *  session: the hook fell back to FALLBACK_SECTIONS and never asked again, so a
 *  single blip at login silently removed every org-specific entry — Agents among
 *  them — until a browser reload. On 17 Sep this endpoint timed out once for the
 *  Trust Care super admin and that is exactly what happened.
 *
 *  So: a short per-attempt timeout (the sidebar is boot-critical — failing fast
 *  and retrying beats hanging for 30s), three attempts with jittered backoff for
 *  transient faults only, and a refresh when the tab comes back if we are still
 *  running on the fallback. */
const NAV_MAX_ATTEMPTS = 3;
const NAV_RETRY_BASE_MS = 400;
const NAV_TIMEOUT_MS = 12000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function getCacheKey(orgId: number, roleType?: string, orgRoleId?: number | null): string {
  // A numeric org role id is the preferred, unambiguous scope (custom org roles
  // can only be resolved server-side by role_terminology.id).
  if (orgRoleId != null) return `nav:${orgId}:role_id:${orgRoleId}`;
  return `nav:${orgId}:${roleType || 'default'}`;
}

// ============================================================================
// FALLBACK — matches the previous hardcoded sidebar
// ============================================================================

const FALLBACK_SECTIONS: NavConfigSection[] = [
  {
    section_key: 'today',
    section_name: 'Today',
    icon: 'Home',
    display_order: 0,
    items: [
      { page_key: 'home', nav_tab_id: 'v2-home', label: 'My day', icon: 'Home', has_access: true, display_behavior: 'hide', display_order: 0, pinned: false },
      { page_key: 'communications.workspace', nav_tab_id: 'workspace-messaging', label: 'Inbox', icon: 'MessageSquare', has_access: true, display_behavior: 'hide', display_order: 1, pinned: false },
    ],
  },
  {
    section_key: 'work',
    section_name: 'Work',
    icon: 'Briefcase',
    display_order: 10,
    items: [
      { page_key: 'members.list', nav_tab_id: 'members', label: 'Members & cases', icon: 'Users', has_access: true, display_behavior: 'hide', display_order: 10, pinned: false },
      { page_key: 'contacts.list', nav_tab_id: 'contacts', label: 'Contacts', icon: 'Users', has_access: true, display_behavior: 'hide', display_order: 11, pinned: false },
      { page_key: 'care.referrals', nav_tab_id: 'v2-referrals', label: 'Care Coordination', icon: 'ArrowLeftRight', has_access: true, display_behavior: 'hide', display_order: 12, pinned: false },
      { page_key: 'events.calendar', nav_tab_id: 'v2-calendar', label: 'Calendar & visits', icon: 'Calendar', has_access: true, display_behavior: 'hide', display_order: 13, pinned: false },
      { page_key: 'forms.library', nav_tab_id: 'form-library', label: 'Forms & consents', icon: 'FileText', has_access: true, display_behavior: 'hide', display_order: 14, pinned: false },
    ],
  },
  {
    section_key: 'comms',
    section_name: 'Communications',
    icon: 'MessageSquare',
    display_order: 20,
    items: [
      // CSR Inbox is exposed inside Communications Hub → Responses → CSR Inbox
      // (Handoffs). The standalone sidebar entry was a duplicate and was
      // removed in migration 365.
      //
      // Campaign Automation was a standalone item; it now lives inside
      // Communications Hub → Setup → Campaign Set Up → Automation (migration
      // 370). The /referral-automation route is preserved in app.tsx for
      // back-compat with deep links.
      { page_key: 'communications.hub', nav_tab_id: 'v2-comms', label: 'Communications', icon: 'MessageSquare', has_access: true, display_behavior: 'hide', display_order: 20, pinned: false },
      { page_key: 'communications.template_library', nav_tab_id: 'template-library', label: 'Template Library', icon: 'Library', has_access: true, display_behavior: 'hide', display_order: 22, pinned: false },
    ],
  },
  {
    section_key: 'network',
    section_name: 'Network',
    icon: 'MapPin',
    display_order: 30,
    items: [
      { page_key: 'care.providers', nav_tab_id: 'provider-directory', label: 'Provider directory', icon: 'MapPin', has_access: true, display_behavior: 'hide', display_order: 30, pinned: false },
      { page_key: 'care.directory', nav_tab_id: 'directory', label: 'My providers', icon: 'LayoutDashboard', has_access: true, display_behavior: 'hide', display_order: 31, pinned: false },
    ],
  },
  {
    section_key: 'activity_tracking',
    section_name: 'Activity Tracking',
    icon: 'ClipboardList',
    display_order: 35,
    items: [
      { page_key: 'activity_tracking.outreach', nav_tab_id: 'outreach-activities', label: 'Outreach Activities', icon: 'ClipboardList', has_access: true, display_behavior: 'hide', display_order: 10, pinned: false },
      { page_key: 'activity_tracking.events', nav_tab_id: 'event-tracking', label: 'Hub Meetings & Trainings', icon: 'Calendar', has_access: true, display_behavior: 'hide', display_order: 20, pinned: false },
      { page_key: 'activity_tracking.my_forms', nav_tab_id: 'my-forms', label: 'My Forms', icon: 'ClipboardList', has_access: true, display_behavior: 'hide', display_order: 30, pinned: false },
    ],
  },
  {
    section_key: 'more',
    section_name: 'More',
    icon: 'MoreHorizontal',
    display_order: 40,
    items: [
      // Team dashboard (dashboards.supervisor) and Nightly Data Ingestion
      // (integrations.data_ingestion) were removed from the sidebar for all
      // orgs — migration 581 mirrors this on the DB side. Their routes stay
      // reachable via the Command Palette / deep links.
      { page_key: 'analytics.reports', nav_tab_id: 'analytics', label: 'Reports', icon: 'BarChart3', has_access: true, display_behavior: 'hide', display_order: 41, pinned: false },
      // Public-site content admin (blog articles + WeaveIn.ai resources) is
      // intentionally NOT in the sidebar — too niche for day-to-day. It is
      // accessible via the Command Palette (Cmd+K → "Public Site Content").
      // Migration 371 mirrors this on the DB side.
      { page_key: 'admin.preferences', nav_tab_id: 'user-preferences', label: 'Preferences', icon: 'User', has_access: true, display_behavior: 'hide', display_order: 44, pinned: false },
    ],
  },
];

// Employer & Sponsor orgs are not in the CBO nav-config (their pages have no
// nav_section), so V2 renders these curated sections by org type. nav_tab_id
// maps to the activeTab branch in app.tsx (e.g. employer 'home' →
// EmployerJourneyDashboard). Mirrors the V1 Employer Hub / sponsor items.
const EMPLOYER_SECTIONS: NavConfigSection[] = [
  {
    section_key: 'employer',
    section_name: 'Employer Hub',
    icon: 'Building2',
    display_order: 0,
    items: [
      { page_key: 'employer.dashboard', nav_tab_id: 'home', label: 'Benefits Hub', icon: 'Building2', has_access: true, display_behavior: 'hide', display_order: 0, pinned: false },
      { page_key: 'employer.retention', nav_tab_id: 'employee-retention', label: 'Benefits Analytics', icon: 'BarChart3', has_access: true, display_behavior: 'hide', display_order: 1, pinned: false },
      { page_key: 'employer.workflows', nav_tab_id: 'employer-workflow-orchestration', label: 'Enrollment & Referrals', icon: 'Play', has_access: true, display_behavior: 'hide', display_order: 2, pinned: false },
    ],
  },
];

const SPONSOR_SECTION: NavConfigSection = {
  section_key: 'sponsor',
  section_name: 'Sponsor',
  icon: 'Briefcase',
  display_order: 5,
  items: [
    { page_key: 'sponsor.dashboard', nav_tab_id: 'sponsor-dashboard', label: 'Sponsor Dashboard', icon: 'Briefcase', has_access: true, display_behavior: 'hide', display_order: 0, pinned: false },
    { page_key: 'care.network', nav_tab_id: 'network-overview', label: 'Network Overview', icon: 'Network', has_access: true, display_behavior: 'hide', display_order: 1, pinned: false },
    { page_key: 'sponsor.retention', nav_tab_id: 'sponsor-retention', label: 'Retention Intelligence', icon: 'AlertTriangle', has_access: true, display_behavior: 'hide', display_order: 2, pinned: false },
  ],
};

// ============================================================================
// HOOK
// ============================================================================

export function useNavConfig(
  organizationId: number | null,
  roleType?: string,
  orgType?: 'cbo' | 'employer' | 'sponsor',
  /**
   * Preferred over `roleType` when previewing a custom org role. Custom roles
   * (e.g. `ecm_care_manager`) are not valid system `role_type`s, so the backend
   * rejects `?role_type=<custom>` with a 400 — it can only resolve them via
   * `?org_role_id=<role_terminology.id>`. Pass this when previewing so the
   * sidebar matches the nav-layout admin (which already scopes by id).
   */
  orgRoleId?: number | null
): {
  sections: NavConfigSection[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [sections, setSections] = useState<NavConfigSection[]>(FALLBACK_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  /** True while we are showing FALLBACK_SECTIONS because the fetch failed. */
  const degradedRef = useRef(false);

  const fetchConfig = useCallback(async () => {
    // Employer orgs get a dedicated hub (not in the CBO nav-config).
    if (orgType === 'employer') {
      setSections(EMPLOYER_SECTIONS);
      setLoading(false);
      return;
    }
    if (!organizationId) {
      setSections(orgType === 'sponsor' ? [SPONSOR_SECTION] : FALLBACK_SECTIONS);
      setLoading(false);
      return;
    }

    // Sponsor orgs are CBOs with extra sponsor surfaces — append a Sponsor
    // section to the resolved CBO nav.
    const withSponsor = (secs: NavConfigSection[]) =>
      orgType === 'sponsor' ? [...secs, SPONSOR_SECTION] : secs;

    const cacheKey = getCacheKey(organizationId, roleType, orgRoleId);
    const cached = navConfigCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setSections(withSponsor(cached.data.sections));
      setLoading(false);
      return;
    }

    // Prefer org_role_id (resolves any custom org role); fall back to the
    // legacy role_type string only when no id is available.
    const url =
      orgRoleId != null
        ? `/api/organizations/${organizationId}/nav-config?org_role_id=${orgRoleId}`
        : roleType
        ? `/api/organizations/${organizationId}/nav-config?role_type=${roleType}`
        : `/api/organizations/${organizationId}/nav-config`;

    try {
      setLoading(true);

      let lastErr: any;
      for (let attempt = 1; attempt <= NAV_MAX_ATTEMPTS; attempt++) {
        try {
          const response = await apiClient.get(url, {
            headers: { 'X-CBO-ID': organizationId.toString() },
            timeout: NAV_TIMEOUT_MS,
          });

          const data: NavConfigResponse = response.data;

          navConfigCache.set(cacheKey, { data, timestamp: Date.now() });

          if (mountedRef.current) {
            degradedRef.current = false;
            setSections(withSponsor(data.sections.length > 0 ? data.sections : FALLBACK_SECTIONS));
            setLoading(false);
            setError(null);
          }
          return;
        } catch (err: any) {
          lastErr = err;
          // Only transient faults are worth another round trip. A 400 from an
          // unresolvable preview role, or a 403, will fail the same way again.
          if (!parseApiError(err).retryable || attempt === NAV_MAX_ATTEMPTS) break;
          if (!mountedRef.current) return;
          const backoff = NAV_RETRY_BASE_MS * 2 ** (attempt - 1);
          await sleep(backoff + Math.random() * NAV_RETRY_BASE_MS);
        }
      }

      throw lastErr;
    } catch (err: any) {
      console.warn('Nav config fetch failed, using fallback:', err?.message);
      // Surface to backend observability (observability_issues → RCA pipeline)
      // so a silently-degraded sidebar is caught instead of hidden behind the
      // fallback. e.g. previewing an unresolvable role 400s here — exactly the
      // class of bug the fallback used to mask. Best-effort, fire-and-forget.
      const statusCode = err?.response?.status ?? err?.status;
      import('@/src/lib/activityTracker')
        .then((m) =>
          m.trackError({
            error_type: 'NavConfigFetchFailed',
            error_message: `nav-config fetch failed, using fallback: ${err?.message || String(err)}`,
            path: url,
            status_code: statusCode,
            method: 'GET',
            stack: err?.stack,
          }),
        )
        .catch(() => {});
      if (mountedRef.current) {
        // Remember that this sidebar is the generic fallback, not the org's own
        // layout, so returning to the tab gives it another chance.
        degradedRef.current = true;
        setSections(withSponsor(FALLBACK_SECTIONS));
        setLoading(false);
        setError(err?.message);
      }
    }
  }, [organizationId, roleType, orgType, orgRoleId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchConfig();
    return () => { mountedRef.current = false; };
  }, [fetchConfig]);

  // A sidebar stuck on the fallback is the user's whole navigation for as long
  // as the tab stays open. Give it another try when they come back to the tab
  // or the connection returns — but only while it is actually degraded, so a
  // healthy sidebar costs nothing.
  useRefreshOnFocus(() => {
    if (degradedRef.current) {
      clearNavConfigCache(organizationId || undefined);
      fetchConfig();
    }
  }, { minIntervalMs: 30_000 });

  // Listen for permission/nav updates to auto-refresh
  useEffect(() => {
    const handleUpdate = (event: CustomEvent<{ orgId?: number }>) => {
      if (!event.detail?.orgId || event.detail.orgId === organizationId) {
        clearNavConfigCache(organizationId || undefined);
        fetchConfig();
      }
    };

    window.addEventListener('pagePermissionsUpdated', handleUpdate as EventListener);
    window.addEventListener('navConfigUpdated', handleUpdate as EventListener);
    return () => {
      window.removeEventListener('pagePermissionsUpdated', handleUpdate as EventListener);
      window.removeEventListener('navConfigUpdated', handleUpdate as EventListener);
    };
  }, [organizationId, fetchConfig]);

  return { sections, loading, error, refetch: fetchConfig };
}

/**
 * Clear nav config cache
 */
export function clearNavConfigCache(orgId?: number): void {
  if (orgId) {
    for (const key of navConfigCache.keys()) {
      if (key.startsWith(`nav:${orgId}:`)) {
        navConfigCache.delete(key);
      }
    }
  } else {
    navConfigCache.clear();
  }
}

export default useNavConfig;
